import { QueryClientProvider } from '@tanstack/react-query';
import { type ComponentType, lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { ColumnaDelPaciente } from './components/domain/ColumnaDelPaciente.js';
import { AvisoDeActualizacion } from './components/ui/AvisoDeActualizacion.js';
import { Cargando, SinConexion } from './components/ui/Estado.js';
import { LimiteDeError } from './components/ui/LimiteDeError.js';
import { VaciadoDeLaCola } from './formularios/VaciadoDeLaCola.js';
import { clienteDeConsultas } from './lib/cliente-de-consultas.js';
import { cargarConRecuperacion } from './lib/pwa.js';
import { leerSesion } from './lib/sesion.js';
import { Onboarding } from './onboarding/Onboarding.js';
import { rolesQueVen } from './rutas/destinos.js';
import { ExigirRol, INICIO_DEL_ROL, useYoConReintento } from './rutas/sesion.js';

/**
 * Raíz de la PWA · un árbol de rutas por rol (ADR-015).
 *
 * Un solo `QueryClient` para toda la app (ADR-013): todo dato del servidor pasa
 * por queries y mutations, y el estado local de una pantalla se queda en React.
 * Vive en `lib/cliente-de-consultas.ts` porque el vaciado de la cola también lo
 * necesita, y su consecuencia —recargar `mi` cuando algo sale— tiene que estar
 * en un solo lugar.
 *
 * **Cada rama se carga aparte** (`React.lazy`). No es micro-optimización: el
 * paciente entra desde un celular con una conexión mala, y no tiene por qué
 * descargar el workspace clínico para ver su check-in del día.
 *
 * **Y cada rama se recupera si su chunk no está** (P-65): con la app abierta
 * durante un deploy, la pantalla que todavía no se había cargado pide un archivo
 * que ya no existe. `pantalla()` recarga una vez contra la red y, si ni así,
 * `LimiteDeError` lo dice con un "Reintentar". Nunca una pantalla en blanco.
 *
 * **El guard decide qué se dibuja; la autorización la hace el backend.** Cada
 * endpoint exige su permiso de la matriz de E2.4. Una ruta escondida no es una
 * ruta protegida, y si esto fuera lo único, alcanzaría con escribir la URL.
 */

/** `lazy` con recuperación de chunk (P-65): toda pantalla de la app se carga por acá. */
function pantalla<T extends ComponentType>(importar: () => Promise<{ default: T }>) {
  return lazy(cargarConRecuperacion(importar));
}

const LayoutDelPaciente = pantalla(() =>
  import('./rutas/paciente/Layout.js').then((m) => ({ default: m.LayoutDelPaciente })),
);
const InicioDelPaciente = pantalla(() =>
  import('./rutas/paciente/Inicio.js').then((m) => ({ default: m.InicioDelPaciente })),
);
const Progreso = pantalla(() =>
  import('./rutas/paciente/Progreso.js').then((m) => ({ default: m.Progreso })),
);
const Perfil = pantalla(() =>
  import('./rutas/paciente/Perfil.js').then((m) => ({ default: m.Perfil })),
);
const Reportar = pantalla(() =>
  import('./rutas/paciente/Reportar.js').then((m) => ({ default: m.Reportar })),
);
const Responder = pantalla(() =>
  import('./rutas/paciente/Responder.js').then((m) => ({ default: m.Responder })),
);
const Pia = pantalla(() => import('./rutas/paciente/Pia.js').then((m) => ({ default: m.Pia })));

const LayoutDelEquipo = pantalla(() =>
  import('./rutas/medico/Layout.js').then((m) => ({ default: m.LayoutDelEquipo })),
);
const Dashboard = pantalla(() =>
  import('./rutas/medico/Dashboard.js').then((m) => ({ default: m.Dashboard })),
);
const Pacientes = pantalla(() =>
  import('./rutas/medico/Pacientes.js').then((m) => ({ default: m.Pacientes })),
);
const Operacion = pantalla(() =>
  import('./rutas/medico/Operacion.js').then((m) => ({ default: m.Operacion })),
);
const Workspace = pantalla(() =>
  import('./rutas/medico/Workspace.js').then((m) => ({ default: m.Workspace })),
);
const TabContexto = pantalla(() =>
  import('./rutas/medico/TabContexto.js').then((m) => ({ default: m.TabContexto })),
);
const TabHistoria = pantalla(() =>
  import('./rutas/medico/TabHistoria.js').then((m) => ({ default: m.TabHistoria })),
);
const TabEscalas = pantalla(() =>
  import('./rutas/medico/TabEscalas.js').then((m) => ({ default: m.TabEscalas })),
);
const TabCuestionarios = pantalla(() =>
  import('./rutas/medico/TabCuestionarios.js').then((m) => ({ default: m.TabCuestionarios })),
);
const TabNotas = pantalla(() =>
  import('./rutas/medico/TabNotas.js').then((m) => ({ default: m.TabNotas })),
);
const TabAcciones = pantalla(() =>
  import('./rutas/medico/TabAcciones.js').then((m) => ({ default: m.TabAcciones })),
);

/** `/entrar`, la puerta del equipo (E3.24). Aparte: el paciente nunca la descarga. */
const Entrar = pantalla(() => import('./rutas/Entrar.js').then((m) => ({ default: m.Entrar })));

const Alta = pantalla(() =>
  import('./rutas/medico/alta/Alta.js').then((m) => ({ default: m.Alta })),
);

// La administración de Fundación (ADMIN-1): su propia rama, que ni el equipo
// clínico ni el paciente descargan.
const PanelDeLaRed = pantalla(() =>
  import('./rutas/fundacion/Panel.js').then((m) => ({ default: m.PanelDeLaRedDeFundacion })),
);
const Instituciones = pantalla(() =>
  import('./rutas/fundacion/Instituciones.js').then((m) => ({ default: m.Instituciones })),
);
const Institucion = pantalla(() =>
  import('./rutas/fundacion/Institucion.js').then((m) => ({ default: m.Institucion })),
);
const EquiposProximamente = pantalla(() =>
  import('./rutas/fundacion/Proximamente.js').then((m) => ({ default: m.EquiposProximamente })),
);
const ContenidosProximamente = pantalla(() =>
  import('./rutas/fundacion/Proximamente.js').then((m) => ({
    default: m.ContenidosProximamente,
  })),
);
const SistemaProximamente = pantalla(() =>
  import('./rutas/fundacion/Proximamente.js').then((m) => ({ default: m.SistemaProximamente })),
);

const InicioDelCuidador = pantalla(() =>
  import('./rutas/cuidador/Inicio.js').then((m) => ({ default: m.InicioDelCuidador })),
);

/**
 * Quién entra a cada pantalla del equipo: los roles que tienen el permiso del
 * endpoint que la pantalla lee (P-66, `rutas/destinos.ts`). Una URL escrita a
 * mano por un rol que no la puede abrir muestra S-10, no una pantalla con 403.
 */
const DEL_WORKSPACE = rolesQueVen('dashboard', 'pacientes', 'operacion');

/**
 * La puerta de entrada.
 *
 * Sin sesión, el onboarding. Con sesión, cada rol a su lugar — el paciente a su
 * home, el profesional a su dashboard, el cuidador a su panel. Redirigir según
 * el rol y no a una pantalla común evita el paso intermedio de "elegí dónde ir",
 * que para un adulto mayor es una decisión que no tiene por qué tomar.
 *
 * Se exporta para poder probarla sola: es el `start_url` de la PWA y lo que
 * muestra sin señal es una promesa al paciente, no un detalle de armado.
 */
export function Entrada() {
  // El mismo hook que usa el guard: el reintento al volver la señal es **uno
  // solo** y vive con la consulta, no copiado acá. Y acá importa todavía más,
  // porque `/` es el `start_url` de la PWA: es la pantalla que se abre al tocar
  // el ícono, y la que más probablemente alguien mire sin señal.
  const { data: yo, isPending, sinRespuestaDelServidor } = useYoConReintento();

  if (isPending) {
    return (
      <ColumnaDelPaciente>
        <Cargando mensaje="Cargando tu sesión…" />
      </ColumnaDelPaciente>
    );
  }
  // **Sólo** la falta de respuesta del servidor (P-99). Cualquier otro error
  // acá sigue llevando al onboarding, que es lo correcto: `/yo` también falla
  // mientras la persona todavía no terminó de darse de alta, y ésa es
  // exactamente la pantalla que tiene que ver.
  if (sinRespuestaDelServidor && leerSesion() !== null) {
    return (
      <ColumnaDelPaciente>
        <SinConexion />
      </ColumnaDelPaciente>
    );
  }
  if (!yo) return <Onboarding />;

  return <Navigate replace to={INICIO_DEL_ROL[yo.rol]} />;
}

export function App() {
  return (
    <QueryClientProvider client={clienteDeConsultas}>
      <BrowserRouter>
        <div className="min-h-dvh bg-background text-foreground">
          {/* Lo respondido sin conexión sale solo, desde cualquier pantalla (H-1). */}
          <VaciadoDeLaCola />
          <AvisoDeActualizacion />
          <LimiteDeError>
            <Suspense fallback={<Cargando />}>
              <Routes>
                <Route
                  element={
                    // La columna del teléfono la pone cada pantalla, no la ruta: la
                    // puerta de entrada usa el ancho en escritorio (DEMO 06/10).
                    <main className="flex min-h-dvh w-full flex-col">
                      <Entrada />
                    </main>
                  }
                  path="/"
                />
                <Route element={<Entrar />} path="/entrar" />

                {/* ── Paciente ───────────────────────────────────────────── */}
                <Route
                  element={
                    <ExigirRol roles={['PACIENTE']}>
                      <LayoutDelPaciente />
                    </ExigirRol>
                  }
                >
                  <Route element={<InicioDelPaciente />} path="/inicio" />
                  <Route element={<Pia />} path="/pia" />
                  <Route element={<Progreso />} path="/pasos" />
                  <Route element={<Perfil />} path="/perfil" />
                  <Route element={<Reportar />} path="/reportar" />
                </Route>

                {/* El check-in y la evaluación, a pantalla completa: sin tab bar,
                  con su propio encabezado de flujo (E3.9, P-73). */}
                <Route
                  element={
                    <ExigirRol roles={['PACIENTE']}>
                      <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col bg-background">
                        <Responder />
                      </main>
                    </ExigirRol>
                  }
                  path="/checkin/:itemId"
                />

                {/* ── Cuidador ───────────────────────────────────────────── */}
                <Route
                  element={
                    <ExigirRol roles={['CUIDADOR']}>
                      <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col">
                        <InicioDelCuidador />
                      </main>
                    </ExigirRol>
                  }
                  path="/cuidador"
                />

                {/* ── Fundación ──────────────────────────────────────────── */}
                <Route
                  element={
                    <ExigirRol roles={['ADMIN_FUNDACION']}>
                      <LayoutDelEquipo />
                    </ExigirRol>
                  }
                  path="/fundacion"
                >
                  <Route element={<PanelDeLaRed />} index />
                  <Route element={<Instituciones />} path="instituciones" />
                  <Route element={<Institucion />} path="instituciones/:id" />
                  <Route element={<EquiposProximamente />} path="equipos" />
                  <Route element={<ContenidosProximamente />} path="contenidos" />
                  <Route element={<SistemaProximamente />} path="sistema" />
                </Route>

                {/* ── Equipo clínico y coordinación ──────────────────────── */}
                <Route
                  element={
                    <ExigirRol roles={DEL_WORKSPACE}>
                      <LayoutDelEquipo />
                    </ExigirRol>
                  }
                  path="/equipo"
                >
                  <Route
                    element={
                      <ExigirRol roles={rolesQueVen('dashboard')}>
                        <Dashboard />
                      </ExigirRol>
                    }
                    index
                  />
                  <Route
                    element={
                      <ExigirRol roles={rolesQueVen('operacion')}>
                        <Operacion />
                      </ExigirRol>
                    }
                    path="operacion"
                  />
                  <Route
                    element={
                      <ExigirRol roles={rolesQueVen('pacientes')}>
                        <Pacientes />
                      </ExigirRol>
                    }
                    path="pacientes"
                  />
                  <Route
                    element={
                      <ExigirRol roles={rolesQueVen('alta')}>
                        <Alta />
                      </ExigirRol>
                    }
                    path="pacientes/nuevo"
                  />
                  <Route
                    element={
                      <ExigirRol roles={rolesQueVen('ficha')}>
                        <Workspace />
                      </ExigirRol>
                    }
                    path="pacientes/:id"
                  >
                    <Route element={<TabContexto />} index />
                    <Route
                      element={
                        <ExigirRol roles={rolesQueVen('historia')}>
                          <TabHistoria />
                        </ExigirRol>
                      }
                      path="historia"
                    />
                    {/* Timeline e Interacciones son Historia desde el 04/10: un
                        enlace viejo llega igual, con su filtro puesto. */}
                    <Route element={<Navigate replace to="../historia" />} path="timeline" />
                    <Route
                      element={
                        <Navigate
                          replace
                          to={{ pathname: '../historia', search: '?filtro=paciente' }}
                        />
                      }
                      path="interacciones"
                    />
                    <Route element={<TabEscalas />} path="escalas" />
                    <Route element={<TabCuestionarios />} path="cuestionarios" />
                    <Route
                      element={
                        <ExigirRol roles={rolesQueVen('notas')}>
                          <TabNotas />
                        </ExigirRol>
                      }
                      path="notas"
                    />
                    <Route
                      element={
                        <ExigirRol roles={rolesQueVen('acciones')}>
                          <TabAcciones />
                        </ExigirRol>
                      }
                      path="acciones"
                    />
                  </Route>
                </Route>

                {/* Cualquier otra cosa vuelve a la entrada, que sabe a dónde va
                  cada rol. Un 404 propio diria "esto no existe" cuando lo que
                  pasa casi siempre es que la persona pego un enlace de otro rol. */}
                <Route element={<Navigate replace to="/" />} path="*" />
              </Routes>
            </Suspense>
          </LimiteDeError>
        </div>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
